import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  IconButton,
  TextField,
  Paper,
  Typography,
  Divider,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  Slider,
  Switch,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  Mic as MicIcon,
} from '@mui/icons-material';
import { alpha, styled } from '@mui/material/styles';
import api from '../../services/api';
import { useSearchSettings } from '../../contexts/SearchSettingsContext';
import { useHelpContent } from '../../utils/HelpUtils';
import { DropdownFieldTooltip } from '../DropdownFieldTooltip';
import AiSparklesIcon from '../icons/AiSparklesIcon';

const ResizablePaper = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  right: 0,
  top: 64,
  height: 'calc(100vh - 64px)',
  width: '400px',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1000,
  boxShadow: theme.shadows[8],
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderLeft: `1px solid ${theme.palette.divider}`,
}));

const ResizeHandle = styled('div')(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: '4px',
  cursor: 'ew-resize',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.35),
  },
}));

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 16px',
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.06) : theme.palette.grey[100],
  borderBottom: `1px solid ${theme.palette.divider}`,
  position: 'relative',
  zIndex: 1001,
}));

const MessagesContainer = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  padding: '16px',
});

const InputContainer = styled(Box)(({ theme }) => ({
  padding: '16px',
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const SettingsContainer = styled(Box)(({ theme }) => ({
  padding: '16px',
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const MessageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  overflowY: 'auto',
  flexGrow: 1,
}));

const MessageBubble = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'type',
})<{ type: 'user' | 'assistant' }>(({ theme, type }) => ({
  padding: theme.spacing(1.5),
  maxWidth: '80%',
  alignSelf: type === 'user' ? 'flex-end' : 'flex-start',
  backgroundColor:
    type === 'user'
      ? theme.palette.primary.main
      : theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.08)
        : theme.palette.grey[100],
  color: type === 'user' ? theme.palette.primary.contrastText : theme.palette.text.primary,
  borderRadius: theme.spacing(2),
  wordBreak: 'break-word',
  border:
    type === 'assistant' && theme.palette.mode === 'dark'
      ? `1px solid ${alpha(theme.palette.common.white, 0.1)}`
      : undefined,
}));

const LoadingIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5),
  maxWidth: '80%',
  alignSelf: 'flex-start',
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.08) : theme.palette.grey[100],
  color: theme.palette.text.primary,
  borderRadius: theme.spacing(2),
}));

const TitleContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const SourceTextsContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(1),
  backgroundColor:
    theme.palette.mode === 'dark' ? alpha(theme.palette.common.black, 0.25) : alpha(theme.palette.common.black, 0.04),
  borderRadius: theme.spacing(1),
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

interface SemanticSearchWindowProps {
  open: boolean;
  onClose: () => void;
  documentId: string;
}

interface Message {
  type: 'user' | 'assistant';
  content: string;
  sourceTexts?: string[];
}

// Store conversation history in localStorage
const getConversationHistory = (documentId: string): Message[] => {
  const history = localStorage.getItem(`conversation_history_${documentId}`);
  return history ? JSON.parse(history) : [];
};

const saveConversationHistory = (documentId: string, messages: Message[]) => {
  localStorage.setItem(`conversation_history_${documentId}`, JSON.stringify(messages));
};

// Helper to detect RTL (Hebrew, Arabic, etc.)
function isRTL(text: string) {
  const rtlChars = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/;
  return rtlChars.test(text);
}

/** Minimal Web Speech API typings (SpeechRecognition / webkitSpeechRecognition). */
interface WebSpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((this: WebSpeechRecognition, ev: WebSpeechRecognitionEvent) => void) | null;
  onerror: ((this: WebSpeechRecognition, ev: WebSpeechRecognitionErrorEvent) => void) | null;
  onend: ((this: WebSpeechRecognition, ev: Event) => void) | null;
}

interface WebSpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: WebSpeechRecognitionResultList;
}

interface WebSpeechRecognitionErrorEvent extends Event {
  error: string;
}

interface WebSpeechRecognitionResultList {
  length: number;
  item(index: number): WebSpeechRecognitionResult;
  [index: number]: WebSpeechRecognitionResult;
}

interface WebSpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  item(index: number): WebSpeechRecognitionAlternative;
  [index: number]: WebSpeechRecognitionAlternative;
}

interface WebSpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

function getWebSpeechRecognitionCtor(): { new (): WebSpeechRecognition } | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: { new (): WebSpeechRecognition };
    webkitSpeechRecognition?: { new (): WebSpeechRecognition };
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Shared with Structure builder so one preference controls speech-to-text everywhere. */
const SPEECH_LANGUAGE_STORAGE_KEY = 'treeAssistantSpeechLanguage';

const SPEECH_LANG_OPTIONS: { code: string; name: string; short: string }[] = [
  { code: 'en-US', name: 'English (US)', short: 'EN' },
  { code: 'he-IL', name: 'Hebrew', short: 'HE' },
  { code: 'es-ES', name: 'Spanish', short: 'ES' },
  { code: 'fr-FR', name: 'French', short: 'FR' },
  { code: 'de-DE', name: 'German', short: 'DE' },
  { code: 'it-IT', name: 'Italian', short: 'IT' },
  { code: 'pt-BR', name: 'Portuguese', short: 'PT' },
  { code: 'ru-RU', name: 'Russian', short: 'RU' },
  { code: 'ja-JP', name: 'Japanese', short: 'JA' },
  { code: 'ko-KR', name: 'Korean', short: 'KO' },
  { code: 'zh-CN', name: 'Chinese', short: 'ZH' },
];

function speechLangOption(code: string) {
  return SPEECH_LANG_OPTIONS.find((o) => o.code === code) ?? SPEECH_LANG_OPTIONS[0];
}

/** Matched min height for composer text field outline and voice control card (Structure builder uses the same). */
const AI_COMPOSER_MIN_HEIGHT = 88;

const aiComposerTextFieldSx = {
  flex: 1,
  '& .MuiOutlinedInput-root': {
    minHeight: AI_COMPOSER_MIN_HEIGHT,
    height: '100%',
    alignItems: 'flex-start',
    boxSizing: 'border-box',
  },
} as const;

const aiComposerVoicePaperSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'center',
  gap: 0.75,
  flexShrink: 0,
  alignSelf: 'stretch',
  minHeight: AI_COMPOSER_MIN_HEIGHT,
  height: '100%',
  px: 0.75,
  py: 0.5,
  borderRadius: 1.5,
  bgcolor: 'background.default',
  boxShadow: 1,
  boxSizing: 'border-box',
} as const;

const aiComposerLangSelectSx = {
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.04em',
  bgcolor: 'background.paper',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
  '& .MuiSelect-select': {
    py: 1,
    px: 1.25,
    minHeight: 'unset',
    lineHeight: 1.3,
    textAlign: 'center',
  },
} as const;

function normalizeSpeechLangCode(raw: string): string {
  const c = raw.trim();
  if (!c) return 'en-US';
  if (SPEECH_LANG_OPTIONS.some((o) => o.code === c)) return c;
  const base = c.split('-')[0]?.toLowerCase() ?? '';
  const match = SPEECH_LANG_OPTIONS.find((o) => o.code.toLowerCase().startsWith(`${base}-`));
  return match?.code ?? 'en-US';
}

function readInitialSpeechLang(): string {
  if (typeof localStorage === 'undefined') {
    return typeof navigator !== 'undefined' ? normalizeSpeechLangCode(navigator.language || 'en-US') : 'en-US';
  }
  const saved = localStorage.getItem(SPEECH_LANGUAGE_STORAGE_KEY);
  if (saved && saved.trim()) return normalizeSpeechLangCode(saved);
  return typeof navigator !== 'undefined'
    ? normalizeSpeechLangCode(navigator.language || 'en-US')
    : 'en-US';
}

const SemanticSearchWindow: React.FC<SemanticSearchWindowProps> = ({ open, onClose, documentId }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [width, setWidth] = useState(400);
  const [isResizing, setIsResizing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { settings, updateSettings } = useSearchSettings();
  const {
    similarityThreshold,
    maxResults,
    autoClearHistory,
    hybridSemanticRanking,
    helpMode,
    shortTooltips,
  } = settings;
  const { getToolbarTooltipTextForState } = useHelpContent();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const resizeRef = useRef<HTMLDivElement>(null);
  const [showSources, setShowSources] = useState(() => {
    const saved = localStorage.getItem('ai_show_sources');
    return saved ? JSON.parse(saved) : false;
  });
  const [showSimilarityScore, setShowSimilarityScore] = useState(() => {
    const saved = localStorage.getItem('ai_show_similarity_score');
    return saved ? JSON.parse(saved) : false;
  });

  const recognitionRef = useRef<WebSpeechRecognition | null>(null);
  const speechBaseRef = useRef('');
  const [isListening, setIsListening] = useState(false);
  const [voiceLang, setVoiceLang] = useState(readInitialSpeechLang);

  const voiceSupported = useMemo(() => !!getWebSpeechRecognitionCtor(), []);

  const abortVoiceInput = useCallback(() => {
    try {
      recognitionRef.current?.abort();
    } catch {
      /* ignore */
    }
    recognitionRef.current = null;
    setIsListening(false);
  }, []);

  const handleVoiceLangChange = useCallback((event: SelectChangeEvent<string>) => {
    const v = normalizeSpeechLangCode(event.target.value);
    setVoiceLang(v);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(SPEECH_LANGUAGE_STORAGE_KEY, v);
    }
    if (recognitionRef.current) {
      recognitionRef.current.lang = v;
    }
  }, []);

  const toggleVoiceInput = useCallback(() => {
    if (isListening) {
      try {
        recognitionRef.current?.stop();
      } catch {
        /* ignore */
      }
      return;
    }
    if (isLoading) return;
    const Ctor = getWebSpeechRecognitionCtor();
    if (!Ctor) return;

    speechBaseRef.current = input;
    const recognition = new Ctor();
    recognitionRef.current = recognition;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = voiceLang;

    recognition.onresult = (event: WebSpeechRecognitionEvent) => {
      const { results } = event;
      let finalText = '';
      let interimText = '';
      for (let i = 0; i < results.length; i++) {
        const r = results[i];
        const t = r[0]?.transcript ?? '';
        if (!t) continue;
        if (r.isFinal) finalText += t;
        else interimText += t;
      }
      const spoken = (finalText + interimText).trim();
      const base = speechBaseRef.current;
      if (!spoken) {
        setInput(base);
        return;
      }
      const needsSpace = base.length > 0 && !/\s$/.test(base) && !/^\s/.test(spoken);
      setInput(base + (needsSpace ? ' ' : '') + spoken);
    };

    recognition.onerror = (event: WebSpeechRecognitionErrorEvent) => {
      if (event.error === 'aborted') {
        recognitionRef.current = null;
        setIsListening(false);
        return;
      }
      console.error('Ask AI speech:', event.error);
      if (event.error === 'not-allowed') {
        setMessages((prev) => [
          ...prev,
          {
            type: 'assistant' as const,
            content:
              'Microphone permission was denied. Allow microphone access for voice questions, or type your message instead.',
          },
        ]);
      }
      try {
        recognition.abort();
      } catch {
        /* ignore */
      }
      recognitionRef.current = null;
      setIsListening(false);
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      setIsListening(false);
    };

    try {
      recognition.start();
      setIsListening(true);
    } catch (e) {
      console.error(e);
      recognitionRef.current = null;
      setIsListening(false);
    }
  }, [isListening, isLoading, input, voiceLang]);

  useEffect(
    () => () => {
      try {
        recognitionRef.current?.abort();
      } catch {
        /* ignore */
      }
      recognitionRef.current = null;
    },
    [],
  );

  useEffect(() => {
    if (!open) {
      abortVoiceInput();
    }
  }, [open, abortVoiceInput]);

  useEffect(() => {
    abortVoiceInput();
  }, [documentId, abortVoiceInput]);
  useEffect(() => {
    if (open && documentId) {
      const history = getConversationHistory(documentId);
      setMessages(history);
    } else {
      setMessages([]);
    }
  }, [documentId, open]);

  // Save conversation history when messages change
  useEffect(() => {
    if (documentId && messages.length > 0) {
      saveConversationHistory(documentId, messages);
    }
  }, [documentId, messages]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  // If showSources is turned off, also turn off showSimilarityScore
  useEffect(() => {
    if (!showSources && showSimilarityScore) {
      setShowSimilarityScore(false);
      localStorage.setItem('ai_show_similarity_score', JSON.stringify(false));
    }
  }, [showSources]);

  const handleResizeStart = (e: React.MouseEvent) => {
    setIsResizing(true);
    const startX = e.clientX;
    const startWidth = width;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = startWidth + (startX - e.clientX);
      if (newWidth >= 300 && newWidth <= 800) {
        setWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isLoading) return;

    abortVoiceInput();
    setInput('');

    const userMessage = { type: 'user' as const, content: text };
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const requestBody = {
        question: text,
        documentId: documentId,
        includeSources: showSources,
      };
      const response = await api.post(`/search/qa`, requestBody);

      const assistantMessage = {
        type: 'assistant' as const,
        content: response.data.answer,
        sourceTexts: response.data.sourceTexts,
      };
      setMessages((prev) => [...prev, assistantMessage]);

      if (autoClearHistory) {
        setMessages([userMessage, assistantMessage]);
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      const status = error?.response?.status;
      const bodyAnswer =
        status === 403 && typeof error?.response?.data?.answer === 'string'
          ? error.response.data.answer
          : null;
      const errorMessage = {
        type: 'assistant' as const,
        content: bodyAnswer || 'Sorry, there was an error processing your request.',
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearHistory = async () => {
    abortVoiceInput();
    try {
      await api.post(`/search/qa/clear-history?documentId=${encodeURIComponent(documentId)}`);
      setMessages([]);
      localStorage.removeItem(`conversation_history_${documentId}`);
    } catch (error) {
      console.error('Error clearing conversation history:', error);
    }
  };

   const handleUpdateSettings = async () => {
      try {
        await api.put('/search/settings', {
            search: {
                maxResults,
                similarityThreshold,
                autoClearHistory
            }
        });
      } catch (error) {
        console.error('Error updating settings:', error);
      }
    };

  if (!open) return null;

  return (
    <ResizablePaper style={{ width }}>
      <ResizeHandle
        ref={resizeRef}
        onMouseDown={handleResizeStart}
      />
      <Header>
        <TitleContainer>
          <AiSparklesIcon sx={{ 
            color: (theme: any) => theme.palette.primary.main,
            '& path:first-of-type': {
              stroke: (theme: any) => theme.palette.primary.main
            }
          }} />
          <Typography variant="h6">Ask AI</Typography>
        </TitleContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip 
            key={`${helpMode}-${shortTooltips}-clear-history`}
            title={getToolbarTooltipTextForState('semantic-clear-history', true)}
          >
            <IconButton 
              onClick={handleClearHistory}
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
          <Tooltip 
            key={`${helpMode}-${shortTooltips}-settings`}
            title={getToolbarTooltipTextForState('semantic-settings', true)}
          >
            <IconButton 
              onClick={() => setShowSettings(!showSettings)}
              size="small"
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          <Tooltip 
            key={`${helpMode}-${shortTooltips}-close`}
            title={getToolbarTooltipTextForState('semantic-close', true)}
          >
            <IconButton 
              onClick={onClose} 
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'text.primary',
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Header>
      <Divider />
      <MessagesContainer>
        <MessageContainer>
          {messages.map((message, index) => (
            <MessageBubble
              key={index}
              type={message.type}
              dir={isRTL(message.content) ? 'rtl' : 'ltr'}
              sx={{
                textAlign: isRTL(message.content) ? 'right' : 'left',
                whiteSpace: 'pre-line',
              }}
            >
              {message.content}
              {showSources && message.type === 'assistant' && Array.isArray(message.sourceTexts) && message.sourceTexts.length > 0 && (
                <SourceTextsContainer>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Source texts ({message.sourceTexts.length}):
                  </Typography>
                  {message.sourceTexts.map((source: any, idx: number) => {
                    let text: string, subcase: string | undefined, score: number | undefined;
                    if (typeof source === 'string') {
                      text = source;
                      subcase = undefined;
                      score = undefined;
                    } else {
                      text = source.text;
                      subcase = source.subcase;
                      score = source.score;
                    }
                    return (
                      <Box key={idx} sx={{ mb: 2 }}>
                        {subcase && (
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, color: 'primary.main', display: 'block', mb: 0.5 }}
                            dir={isRTL(subcase) ? 'rtl' : 'ltr'}
                          >
                            {subcase}:
                          </Typography>
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            p: 1,
                            bgcolor: (t) =>
                              t.palette.mode === 'dark'
                                ? alpha(t.palette.common.black, 0.3)
                                : alpha(t.palette.common.black, 0.04),
                            borderRadius: 1,
                            textAlign: isRTL(text) ? 'right' : 'left',
                            whiteSpace: 'pre-line',
                          }}
                          dir={isRTL(text) ? 'rtl' : 'ltr'}
                        >
                          {text}
                        </Typography>
                        {showSimilarityScore && score !== undefined && (
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', display: 'block', mt: 0.5, ml: 1 }}
                          >
                            Score: {(score * 100).toFixed(1)}%
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </SourceTextsContainer>
              )}
            </MessageBubble>
          ))}
          {isLoading && (
            <LoadingIndicator>
              <CircularProgress size={20} />
              <Typography>Generating answer...</Typography>
            </LoadingIndicator>
          )}
          <div ref={messagesEndRef} />
        </MessageContainer>
      </MessagesContainer>
      {showSettings && (
        <SettingsContainer>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2">
              Search Settings
            </Typography>
            <Tooltip 
              key={`${helpMode}-${shortTooltips}-settings-close`}
              title={getToolbarTooltipTextForState('semantic-settings-close', true)}
            >
              <IconButton 
                size="small"
                onClick={() => setShowSettings(false)}
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'text.primary',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Tooltip 
              key={`${helpMode}-${shortTooltips}-similarity-threshold`}
              title={getToolbarTooltipTextForState('semantic-similarity-threshold', true)}
            >
              <Typography gutterBottom>
                Similarity Threshold: {Math.round(similarityThreshold * 100)}%
              </Typography>
            </Tooltip>
            <Slider
              value={similarityThreshold}
              onChange={(_, value) => updateSettings({similarityThreshold: value as number})}
              onChangeCommitted={(_, value) => {
                updateSettings({similarityThreshold: value as number});
                handleUpdateSettings();
              }}
              min={0}
              max={1}
              step={0.01}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Tooltip 
              key={`${helpMode}-${shortTooltips}-max-results`}
              title={getToolbarTooltipTextForState('semantic-max-results', true)}
            >
              <Typography gutterBottom>
                Maximum Results: {maxResults}
              </Typography>
            </Tooltip>
            <Slider
              value={maxResults}
              onChange={(_, value) => updateSettings({maxResults: value as number})}
              onChangeCommitted={(_, value) => {
                updateSettings({maxResults: value as number});
                handleUpdateSettings();
              }}
              min={1}
              max={10}
              step={1}
            />
            <Typography variant="caption" color="text.secondary">
              Maximum context items to send to AI (not used for Find in Document)
            </Typography>
          </Box>
          <Tooltip
            key={`${helpMode}-${shortTooltips}-auto-clear-history`}
            title={getToolbarTooltipTextForState('semantic-auto-clear-history', autoClearHistory)}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={autoClearHistory}
                  onChange={(e) => {
                    updateSettings({ autoClearHistory: e.target.checked });
                    handleUpdateSettings();
                  }}
                />
              }
              label="Auto Clear History"
            />
          </Tooltip>
          <Tooltip
            key={`${helpMode}-${shortTooltips}-hybrid-ranking`}
            title={getToolbarTooltipTextForState('semantic-hybrid-ranking', hybridSemanticRanking)}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={hybridSemanticRanking !== false}
                  onChange={async (e) => {
                    const checked = e.target.checked;
                    updateSettings({ hybridSemanticRanking: checked });
                    try {
                      await api.put('/search/settings', {
                        search: { hybridSemanticRanking: checked },
                      });
                    } catch (error) {
                      console.error('Error updating hybrid ranking setting:', error);
                    }
                  }}
                />
              }
              label="Prefer exact words (hybrid ranking)"
            />
          </Tooltip>
          <Tooltip
            key={`${helpMode}-${shortTooltips}-show-sources`}
            title={getToolbarTooltipTextForState('semantic-show-sources', showSources)}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={showSources}
                  onChange={e => {
                    setShowSources(e.target.checked);
                    localStorage.setItem('ai_show_sources', JSON.stringify(e.target.checked));
                  }}
                />
              }
              label="Show source texts for answers"
            />
          </Tooltip>
          <Tooltip 
            key={`${helpMode}-${shortTooltips}-show-similarity-score`}
            title={getToolbarTooltipTextForState('semantic-show-similarity-score', showSimilarityScore)}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={showSimilarityScore}
                  onChange={e => {
                    setShowSimilarityScore(e.target.checked);
                    localStorage.setItem('ai_show_similarity_score', JSON.stringify(e.target.checked));
                  }}
                  disabled={!showSources}
                />
              }
              label="Show similarity score for source texts"
              sx={{ ml: 4 }}
            />
          </Tooltip>
        </SettingsContainer>
      )}
      <InputContainer>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch' }}>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex' }}>
            <Tooltip
              key={`${helpMode}-${shortTooltips}-question-input`}
              title={getToolbarTooltipTextForState('semantic-question-input', true)}
              sx={{ flex: 1, display: 'flex', minWidth: 0, width: '100%' }}
            >
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Ask a question about the document..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void handleSend();
                  }
                }}
                multiline
                maxRows={4}
                sx={aiComposerTextFieldSx}
              />
            </Tooltip>
          </Box>
          <Paper variant="outlined" sx={aiComposerVoicePaperSx}>
            <Tooltip
              key={`${helpMode}-${shortTooltips}-semantic-voice`}
              title={
                voiceSupported
                  ? getToolbarTooltipTextForState('semantic-voice', true)
                  : 'Voice input is not supported in this browser. Try Chrome or Edge, or type your question.'
              }
              placement="top"
            >
              <span>
                <IconButton
                  color={isListening ? 'error' : 'default'}
                  aria-label={isListening ? 'Stop voice input' : 'Start voice input'}
                  aria-pressed={isListening}
                  onClick={() => toggleVoiceInput()}
                  disabled={!voiceSupported || isLoading}
                  sx={{ alignSelf: 'center', p: 1.25 }}
                >
                  <MicIcon fontSize="medium" />
                </IconButton>
              </span>
            </Tooltip>
            <DropdownFieldTooltip
              key={`${helpMode}-${shortTooltips}-semantic-voice-language`}
              title={getToolbarTooltipTextForState('semantic-voice-language', true)}
            >
              <FormControl size="small" sx={{ minWidth: 68, width: '100%' }}>
                <Select
                  value={voiceLang}
                  onChange={handleVoiceLangChange}
                  disabled={isLoading || !voiceSupported}
                  aria-label="Voice recognition language"
                  variant="outlined"
                  fullWidth
                  renderValue={(code) => speechLangOption(code).short}
                  sx={aiComposerLangSelectSx}
                >
                  {SPEECH_LANG_OPTIONS.map((o) => (
                    <MenuItem key={o.code} value={o.code}>
                      {o.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </DropdownFieldTooltip>
          </Paper>
          <Tooltip
            key={`${helpMode}-${shortTooltips}-send`}
            title={getToolbarTooltipTextForState('semantic-send', true)}
          >
            <span style={{ alignSelf: 'flex-start' }}>
              <IconButton
                color="primary"
                onClick={() => void handleSend()}
                disabled={!input.trim() || isLoading}
              >
                <SendIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </InputContainer>
    </ResizablePaper>
  );
};

export default SemanticSearchWindow; 